import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import Swal from "sweetalert2";
import EditCashLedgerModal from "./EditCashLedgerModal";
import { TRANSACTION_TYPE } from "../../utils/enum";

const formatAmountDisplay = (amount, type = "") => {
  const n = Number(amount) || 0;
  const isNegative = n < 0;
  const isZero = n === 0;

  const formatted = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return {
    display: `${isNegative ? "-" : ""}₹${formatted}`,
    className: type === "credit" ? "text-success-600" : "text-danger-600",
  };
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CashLedgerLayer = () => {
  const { AsyncGetApiCall, AsyncDeleteApiCall, AsyncPatchApiCall } = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [limitVal, setLimitVal] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);

  const fetchCashLedger = async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);

    try {
      const response = await AsyncGetApiCall("/accounts/my-cash-ledger", {
        pageNo: page,
        limitVal: limit,
      });

      if (response?.success) {
        setRows(response.data || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 0);
        setCurrentPage(response.currentPage || 1);
        setHasNextPage(response.hasNextPage || false);
        setHasPrevPage(response.hasPrevPage || false);
      } else {
        setError(response?.message || "Failed to fetch cash ledger");
        setRows([]);
        setTotal(0);
        setTotalPages(0);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "An error occurred while fetching cash ledger";
      setError(msg);
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCashLedger(1, limitVal);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchCashLedger(newPage, limitVal);
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setLimitVal(newLimit);
    fetchCashLedger(1, newLimit);
  };

  const filteredRows = rows.filter((row) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      row.name?.toLowerCase().includes(q) ||
      row.remark?.toLowerCase().includes(q) ||
      row.type?.toLowerCase().includes(q)
    );
  });

  const handleEdit = async (row) => {
    console.log("row", row);
    try {
      const { isConfirmed } = await Swal.fire({
        title: "Edit this Cash entry?",
        text: `This cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#2352c3",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Edit it!",
        customClass: { popup: "swal-sm" },
      });

      if (!isConfirmed) {
        return;
      }
      const result = await AsyncPatchApiCall(`/accounts/edit-entry/${row._id}`, {
        amount: row?.amount,
      });
      if (result?.success) {
        toast.success(result?.message || "Cash ledger edited successfully");
        fetchCashLedger(currentPage, limitVal);
      } else {
        toast.error(result?.message || "Failed to edit cash ledger");
      }
    } catch (err) {
      console.error("Something went wrong", err);
    }
    finally {
      setSelectedLedger(null);
      setShowEditModal(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const { isConfirmed } = await Swal.fire({
        title: "Delete this Cash entry?",
        text: `This cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#2352c3",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Delete it!",
        customClass: { popup: "swal-sm" },
      });

      if (!isConfirmed) {
        return;
      }
      const result = await AsyncDeleteApiCall(`/accounts/delete-entry/${id}`);
      if (result?.success) {
        toast.success(result?.message || "Cash ledger deleted successfully");
        fetchCashLedger(currentPage, limitVal);
      } else {
        toast.error(result?.message || "Failed to delete cash ledger");
      }
    } catch (err) {
      console.error("Something went wrong", err);
    }
  };

  const startSr = rows.length > 0 ? (currentPage - 1) * limitVal + 1 : 0;

  return (
    <>
      {loading && <Loader />}

      {/* Breadcrumb */}
      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-24">
        <div className="d-flex align-items-center gap-2 text-secondary-light">
          <Icon icon="tabler:layout-grid" className="icon text-xl" />
          <Icon icon="tabler:chevron-right" className="icon text-sm" />
          <span className="fw-semibold text-primary-light">Cash Ledger</span>
        </div>
      </div>

      {/* Main Card */}
      <div className="card border shadow-sm radius-16">
        <div className="card-body p-24">
          <h6 className="fw-semibold mb-3">Cash Ledger</h6>

          {/* Top Controls */}
          <div className="d-flex flex-column flex-sm-row gap-3 mb-3 align-items-sm-center justify-content-between">
            <div style={{ width: "110px" }}>
              <SearchableSelect
                placeholder="Show"
                value={String(limitVal)}
                showSearch={false}
                onChange={handleLimitChange}
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
              />
            </div>

            <div
              className="position-relative"
              style={{ maxWidth: "320px", width: "100%" }}
            >
              <Icon
                icon="tabler:search"
                className="position-absolute top-50 translate-middle-y ms-3 text-secondary-light"
                style={{ fontSize: "20px" }}
              />
              <input
                type="text"
                placeholder="Search name, remark, type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control radius-12 h-40-px ps-5"
              />
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            {error ? (
              <div className="py-5 text-center text-danger fw-medium">
                {error}
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-secondary-light fw-medium">
                  No data available in table
                </p>
              </div>
            ) : (
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="fw-bold text-primary-light">SR</th>
                    <th className="fw-bold text-primary-light">Name</th>
                    <th className="fw-bold text-primary-light">Date</th>
                    <th className="fw-bold text-primary-light">Type</th>
                    <th className="fw-bold text-primary-light text-end">
                      Amount (₹)
                    </th>
                    <th className="fw-bold text-primary-light">Remark</th>
                    <th className="fw-bold text-primary-light text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, index) => (
                    <tr key={row._id}>
                      <td className="fw-medium text-secondary-light">
                        {startSr + index}
                      </td>
                      <td className="fw-medium">{row.name}</td>
                      <td className="text-secondary-light">
                        {formatDateTime(row.date)}
                      </td>
                      <td className="text-capitalize fw-medium">{row.type}</td>
                      <td
                        className={`fw-bold text-end ${formatAmountDisplay(row.amount, row.type).className}`}
                      >
                        {formatAmountDisplay(row.amount, row.type).display}
                      </td>
                      <td
                        className="text-secondary-light text-truncate"
                        style={{ maxWidth: "280px" }}
                        title={row.remark || ""}
                      >
                        {row.remark || "—"}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          {row.type === TRANSACTION_TYPE.TRANSFER && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedLedger(row);
                                setShowEditModal(true);
                              }
                              }
                              className="btn btn-success btn-sm radius-8 px-3 py-1"
                            >
                              <Icon icon="tabler:edit" width={18} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(row._id)}
                            className="ledger-action-btn"
                          >
                            <Icon icon="tabler:trash" width={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mt-4 pt-4 border-top">
            <p className="text-secondary-light mb-0">
              Showing{" "}
              <span className="fw-semibold text-dark">
                {rows.length > 0
                  ? `${(currentPage - 1) * limitVal + 1} to ${Math.min(
                    currentPage * limitVal,
                    total,
                  )} of ${total}`
                  : "0 to 0"}{" "}
                entries
              </span>
            </p>

            <div className="d-flex align-items-center gap-1 flex-wrap">
              {/* Previous Button */}
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage || loading}
                className="btn border radius-8 px-3 py-2 d-flex align-items-center justify-content-center"
              >
                <Icon icon="tabler:chevron-left" width={18} />
              </button>

              {/* Page Numbers */}
              {totalPages > 0 &&
                Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2)
                    pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`btn radius-8 px-3 py-2 fw-medium ${pageNum === currentPage ? "btn-primary" : "border"
                        }`}
                      style={
                        pageNum === currentPage
                          ? { backgroundColor: "#043ba9", border: "none" }
                          : {}
                      }
                    >
                      {pageNum}
                    </button>
                  );
                })}

              {/* Ellipsis & Last Page */}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="px-2 text-secondary-light">...</span>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={loading}
                    className="btn border radius-8 px-3 py-2 fw-medium"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              {/* Next Button */}
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage || loading}
                className="btn border radius-8 px-3 py-2 d-flex align-items-center justify-content-center"
              >
                <Icon icon="tabler:chevron-right" width={18} />
              </button>
            </div>
          </div>
        </div>
      </div >

      <EditCashLedgerModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        data={selectedLedger}
        onSubmit={(updatedData) => {
          handleEdit(updatedData);
        }}
      />
    </>
  );
};

export default CashLedgerLayer;
